import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import UserSearchInput from './UserSearchInput';
import './SearchFilters.css';

/**
 * SearchFilters Component
 * 
 * Advanced filtering options for message search.
 * 
 * Features:
 * - Filter by sender (user search)
 * - Filter by date range
 * - Filter by message type
 * - Filter by specific thread
 * 
 * Requirements: 5.3
 */
function SearchFilters({ filters, onFiltersChange, onApply, onCancel }) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [selectedSender, setSelectedSender] = useState(null);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSenderSelect = (user) => {
    setSelectedSender(user);
    handleFilterChange('sender', user.userId);
  };

  const handleRemoveSender = () => {
    setSelectedSender(null);
    handleFilterChange('sender', '');
  };

  const handleApply = () => {
    // Only include non-empty filters
    const activeFilters = Object.entries(localFilters).reduce((acc, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    onApply(activeFilters);
  };

  const handleReset = () => {
    const emptyFilters = {
      sender: '',
      dateFrom: '',
      dateTo: '',
      messageType: '',
      threadId: ''
    };
    setLocalFilters(emptyFilters);
    setSelectedSender(null);
    onFiltersChange(emptyFilters);
  };

  return (
    <Card className="search-filters-card mb-3">
      <Card.Body>
        <Form>
          <Row>
            {/* Sender Filter */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Filter by Sender</Form.Label>
                {selectedSender ? (
                  <div className="selected-sender d-flex align-items-center justify-content-between p-2 border rounded">
                    <div>
                      <strong>{selectedSender.displayName}</strong>
                      <div className="text-muted small">{selectedSender.email}</div>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleRemoveSender}
                      className="text-danger"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                ) : (
                  <UserSearchInput
                    onUserSelect={handleSenderSelect}
                    excludeUserIds={[]}
                  />
                )}
              </Form.Group>
            </Col>

            {/* Message Type Filter */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Message Type</Form.Label>
                <Form.Select
                  value={localFilters.messageType}
                  onChange={(e) => handleFilterChange('messageType', e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="text">Direct Messages</option>
                  <option value="notification">Notifications</option>
                  <option value="announcement">Announcements</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {/* Date From Filter */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  max={localFilters.dateTo || undefined}
                />
              </Form.Group>
            </Col>

            {/* Date To Filter */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  min={localFilters.dateFrom || undefined}
                  max={new Date().toISOString().split('T')[0]}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Action Buttons */}
          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleReset}
            >
              Reset
            </Button>
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={onCancel}
                className="me-2"
              >
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApply}
              >
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                Apply Filters
              </Button>
            </div>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default SearchFilters;
